/**
 * Cliente HTTP configurado con Axios
 * Maneja todas las peticiones al backend
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config, log } from '../../config/env';

/**
 * Instancia de Axios configurada
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: `${config.apiUrl}/api/v1`,
  timeout: config.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor de requests
 * Agrega el token de autenticación si existe
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Obtener token del localStorage
    const token = localStorage.getItem('token');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log de request en desarrollo
    log.debug('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data,
    });

    return config;
  },
  (error: AxiosError) => {
    log.error('Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Interceptor de responses
 * Maneja errores globales y respuestas
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log de response exitoso en desarrollo
    log.debug('API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });

    return response;
  },
  (error: AxiosError) => {
    // Log de error
    log.error('API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    });

    // Manejo de errores específicos
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Token inválido o expirado
          log.warn('Unauthorized - Redirecting to login');
          localStorage.removeItem('token');
          // Opcional: redirigir a login
          // window.location.href = '/login';
          break;

        case 403:
          // Sin permisos
          log.warn('Forbidden - Insufficient permissions');
          break;

        case 404:
          // Recurso no encontrado
          log.warn('Resource not found');
          break;

        case 500:
          // Error del servidor
          log.error('Server error');
          break;

        default:
          log.error('API Error:', error.response.status);
      }
    } else if (error.request) {
      // Request hecho pero sin respuesta
      log.error('No response from server');
    } else {
      // Error al configurar el request
      log.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;

